import { useEffect, useRef } from "react";
import { useState } from "react";
import { Column, createRecord, ResultTable, ScanRecord, useMaxRecords } from "./ResultTableNew";
import { useScanQueueConsumer } from "./useScanQueueConsumer";
import { parseException } from "utils/newExceptionUtils";
import { ScanInput } from "./ScanInput";
import { isValidBarCode, isBagNumber } from "../../../utils/BarCodeUtil";
import { t } from '../../../i18n';
import { buildClientError, ClientError, ScanResultEnvelope, UseScanResultPromptResult } from '../scanResult';
import { playScanFailedAoSound } from "../../../utils/scanUtils";

type ArrivalScanComponentProps = {
    value1Placeholder: string;
    value1ColumnTitle: string;
    /** 后端返回结构改为 ScanResult 外壳，业务异常以 envelope.success=false 表达，不再走 GraphQL errors。 */
    handleScanEvent: (barCode: string) => Promise<ScanResultEnvelope<unknown>>;
    /** 上层注入的扫描结果提示状态机，4 个 PDA 入口共用同一套 */
    scanPrompt: UseScanResultPromptResult;
    /** 可选，扩展成功记录的额外字段（如 [{ title: 'Type', key: 'type' }]） */
    successExtraFields?: { title: string; key: string }[];
    /** 是否开启同时称重模式 */
    weighSimultaneously?: boolean;
    /** 当前重量数据 */
    currentWeight?: { weight: number, unit: string } | null;
    /** 蓝牙连接状态 */
    isBluetoothConnected?: boolean;
}

export const ArrivalScanWightComponent: React.FC<ArrivalScanComponentProps> = ({
    value1Placeholder,
    value1ColumnTitle,
    handleScanEvent,
    scanPrompt,
    successExtraFields,
    weighSimultaneously,
    currentWeight,
    isBluetoothConnected
}) => {
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [totalSuccessCount, setTotalSuccessCount] = useState(0);
    const [totalFailedCount, setTotalFailedCount] = useState(0);
    const [value1, setValue1] = useState('');

    const maxRecords = useMaxRecords();

    const handleScanEventRef = useRef(handleScanEvent);
    handleScanEventRef.current = handleScanEvent;
    const scanPromptRef = useRef(scanPrompt);
    scanPromptRef.current = scanPrompt;
    const successExtraFieldsRef = useRef(successExtraFields);
    successExtraFieldsRef.current = successExtraFields;
    const weighSimultaneouslyRef = useRef(weighSimultaneously);
    weighSimultaneouslyRef.current = weighSimultaneously;
    const currentWeightRef = useRef(currentWeight);
    currentWeightRef.current = currentWeight;
    const maxRecordsRef = useRef(maxRecords);
    maxRecordsRef.current = maxRecords;

    /**
     * 飞行中的 barCode 集合。一次扫描从入队到 onProcess.finally 之间该 barCode 视为在飞行；
     * 期间再扫同 barCode 触发 NOTICE，不入队、不调后端、不记录列表。
     */
    const inFlightCodesRef = useRef<Set<string>>(new Set());

    /** 按 seq 降序插入记录，保留最多 maxSize 条 */
    const insertBySeq = (prev: ScanRecord[], record: ScanRecord, maxSize: number): ScanRecord[] => {
        const seq = (record as any).seq ?? 0;
        const idx = prev.findIndex(r => ((r as any).seq ?? 0) < seq);
        const result = idx === -1 ? [...prev, record] : [...prev.slice(0, idx), record, ...prev.slice(idx)];
        return result.slice(0, maxSize);
    };

    const { enqueueScan } = useScanQueueConsumer({
        maxConcurrency: 5,
        onProcess: async (event) => {
            const barCode = event.code;
            try {
                const env = await handleScanEventRef.current(barCode);
                scanPromptRef.current.showResult(env, barCode);

                if (env.success) {
                    // 成功 → 成功列
                    const extra: Record<string, any> = {};
                    const fields = successExtraFieldsRef.current;
                    if (fields) {
                        const envAny = env as any;
                        fields.forEach(f => {
                            // 兼容旧逻辑：扩展字段从 envelope 顶层 / data / 自定义字段读取
                            const val = envAny[f.key] ?? envAny.data?.[f.key];
                            if (val !== undefined) extra[f.key] = val;
                        });
                    }
                    if (weighSimultaneouslyRef.current && currentWeightRef.current && !isBagNumber(barCode)) {
                        extra.weight = `${currentWeightRef.current.weight} ${currentWeightRef.current.unit}`;
                    }
                    const record = { ...createRecord({ value1: barCode, ...extra }), seq: event.seq } as ScanRecord;
                    setSuccessRecords(prev => insertBySeq(prev, record, maxRecordsRef.current));
                    setTotalSuccessCount(prev => prev + 1);
                } else if ((env.data as any)?.promptInfo || env.message) {
                    // 失败且有可展示内容（后端 data.promptInfo / systemError）→ 失败列
                    playScanFailedAoSound();
                    const pi = (env.data as any)?.promptInfo;
                    const reason = [pi?.title, pi?.content].filter(Boolean).join('，') || env.message || '';
                    const record = { ...createRecord({ value1: barCode, failedReason: reason }), seq: event.seq } as ScanRecord;
                    setFailedRecords(prev => insertBySeq(prev, record, maxRecordsRef.current));
                    setTotalFailedCount(prev => prev + 1);
                }
                // success=false 且无 promptInfo / message → 视为前端预校验拦死且已自行提示，不记录列表（保持旧行为）
            } catch (error) {
                // 仅网络 / Apollo 异常会落到这里；业务异常已由后端走 envelope.success=false 通道
                playScanFailedAoSound();
                const { message: errorMessage } = parseException(error);
                scanPromptRef.current.showResult(buildClientError(ClientError.NETWORK_ERROR, {}, ClientError.ARRIVAL_SCAN_TITLE), barCode);
                const record = { ...createRecord({ value1: barCode, failedReason: errorMessage }), seq: event.seq } as ScanRecord;
                setFailedRecords(prev => insertBySeq(prev, record, maxRecordsRef.current));
                setTotalFailedCount(prev => prev + 1);
            } finally {
                inFlightCodesRef.current.delete(barCode);
            }
        },
    });

    // 当同时称重开关变化时，清空已扫描列表
    useEffect(() => {
        setSuccessRecords([]);
        setFailedRecords([]);
    }, [weighSimultaneously]);

    const handleScan = (barCode: string) => {
        // 兼容收起键盘
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setValue1(barCode);

        if (!isValidBarCode(barCode)) {
            scanPromptRef.current.showResult(buildClientError(ClientError.INVALID_BAR_CODE), barCode);
            return;
        }

        // 飞行中的相同 barCode：弹 NOTICE 黑底 1.5s 提示，不入队、不调后端、不记录列表
        if (inFlightCodesRef.current.has(barCode)) {
            scanPromptRef.current.showNotice(t('pda.scan.duplicateScan'), barCode);
            return;
        }

        // 新扫描发起 → 清旧弹窗，标记 barCode 入飞行
        scanPromptRef.current.clearPrompt();
        inFlightCodesRef.current.add(barCode);
        enqueueScan(barCode);
    };

    // 动态拼接成功列
    const successColumns: Column[] = [
        { title: value1ColumnTitle, key: 'value1' },
        ...(successExtraFields || []),
        { title: t('pda.common.time'), key: 'time' }
    ];

    const failedColumns = [
        { title: value1ColumnTitle, key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        { title: t('pda.common.failedReason'), key: 'failedReason' }
    ];

    return (
        <div>
            {/* 同时称重标签和蓝牙状态提示 */}
            {weighSimultaneously ? (
                <div className="bg-gray-50 px-4 py-2 rounded-lg mb-2">
                    <div className="text-sm text-green-600 mb-1 text-center">{t('pda.pages.pickUpArrival.weighSimultaneously')}</div>
                    {!isBluetoothConnected && (
                        <div className="flex items-center justify-center text-orange-500 text-sm">
                            <span className="mr-1">⚠️</span>
                            <span>{t('pda.pages.pickUpArrival.bluetoothNotConnected')}</span>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-gray-50 px-4 py-2 rounded-lg mb-2">
                    <div className="text-sm text-green-600 text-center">{t('pda.pages.pickUpArrival.notWeighing')}</div>
                </div>
            )}

            <ScanInput placeholder={value1Placeholder} value={value1} onScan={handleScan}/>

            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
                totalSuccessCount={totalSuccessCount}
                totalFailedCount={totalFailedCount}
            />
        </div>
    );
};
